import { INetworkCapture } from '../../domain/interfaces/INetworkCapture';
import { LogEntry, LogLevel } from '../../domain/types';

export class NetworkCapture implements INetworkCapture {
  private callback: ((entry: LogEntry) => void) | null = null;
  private isActive = false;
  private originalFetch: typeof fetch;
  private originalOpen: typeof XMLHttpRequest.prototype.open;
  private originalSend: typeof XMLHttpRequest.prototype.send;

  constructor() {
    this.originalFetch = window.fetch;
    this.originalOpen = XMLHttpRequest.prototype.open;
    this.originalSend = XMLHttpRequest.prototype.send;
  }

  start(callback: (entry: LogEntry) => void): void {
    if (this.isActive) return;
    
    this.callback = callback;
    this.isActive = true;
    
    this.patchFetch();
    this.patchXHR();
  }

  stop(): void {
    if (!this.isActive) return;
    
    this.restoreFetch();
    this.restoreXHR();
    this.callback = null;
    this.isActive = false;
  }

  private patchFetch(): void {
    const self = this;
    window.fetch = function(...args) {
      const startTime = Date.now();
      const [input, init] = args;
      const url = typeof input === 'string' ? input : 
        input instanceof Request ? input.url : input.toString();
      const method = init?.method || 'GET';

      return self.originalFetch.apply(window, args)
        .then(response => {
          const duration = Date.now() - startTime;
          
          // Only log errors (status >= 400)
          if (!response.ok && self.callback) {
            self.callback({
              level: 'error',
              msg: `${method} ${url} ${response.status} (${duration}ms)`,
              ts: new Date().toISOString(),
              source: 'browser',
              meta: {
                type: 'network',
                method,
                requestUrl: url,
                status: response.status,
                duration,
                url: window.location.href
              }
            });
          }
          
          return response;
        })
        .catch(error => {
          const duration = Date.now() - startTime;

          if (self.callback) {
            self.callback({
              level: 'error',
              msg: `${method} ${url} failed (${duration}ms)`,
              ts: new Date().toISOString(),
              source: 'browser',
              meta: {
                type: 'network',
                method,
                requestUrl: url,
                duration,
                error: error.message,
                url: window.location.href
              }
            });
          }

          throw error;
        });
    };
  }

  private restoreFetch(): void {
    window.fetch = this.originalFetch;
  }

  private patchXHR(): void {
    const self = this;
    
    XMLHttpRequest.prototype.open = function(method: string, url: string | URL, ...rest: any[]) {
      (this as any)._method = method;
      (this as any)._url = url.toString();
      (this as any)._startTime = Date.now();
      
      return self.originalOpen.apply(this, [method, url, ...rest] as any);
    };

    XMLHttpRequest.prototype.send = function(body?: any) {
      const xhr = this;
      const method = (xhr as any)._method || 'GET';
      const url = (xhr as any)._url || '';
      const startTime = (xhr as any)._startTime || Date.now();

      // Use { once: true } to automatically remove listeners after they fire
      xhr.addEventListener('loadend', function() {
        const duration = Date.now() - startTime;

        // Only log errors (status >= 400)
        if (xhr.status >= 400 && self.callback) {
          self.callback({
            level: 'error',
            msg: `${method} ${url} ${xhr.status} (${duration}ms)`,
            ts: new Date().toISOString(),
            source: 'browser',
            meta: {
              type: 'network',
              method,
              requestUrl: url,
              status: xhr.status,
              duration,
              size: xhr.responseText?.length,
              url: window.location.href
            }
          });
        }
      }, { once: true });

      xhr.addEventListener('error', function() {
        const duration = Date.now() - startTime;

        if (self.callback) {
          self.callback({
            level: 'error',
            msg: `${method} ${url} failed (${duration}ms)`,
            ts: new Date().toISOString(),
            source: 'browser',
            meta: {
              type: 'network',
              method,
              requestUrl: url,
              duration,
              error: 'Network error',
              url: window.location.href
            }
          });
        }
      }, { once: true });

      return self.originalSend.apply(this, [body] as any);
    };
  }

  private restoreXHR(): void {
    XMLHttpRequest.prototype.open = this.originalOpen;
    XMLHttpRequest.prototype.send = this.originalSend;
  }
}